import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, Alert, CircularProgress, Tabs, Tab, Paper,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Chip, IconButton, Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import HistoryIcon from "@mui/icons-material/History";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import axios from "../../../config/axiosConfig";
import { useAuth } from "../../../context/AuthContext";
import EnviarTraspasoDialog from "../components/EnviarTraspasoDialog";
import RecibirTraspasoDialog from "../components/RecibirTraspasoDialog";
import RegistrarProductoDialog from "../components/RegistrarProductoDialog";
import EditarProductoDialog from "../components/EditarProductoDialog";
import AgregarCantidadDialog from "../components/AgregarCantidadDialog";
import HistorialProductoDialog from "../components/HistorialProductoDialog";

const SUCURSALES = ["La Paz", "Cochabamba", "Santa Cruz", "La Paz(El Alto)"];

const Th = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.78rem",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const formatFechaHora = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const InventarioInternoPage = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);

  const [productos, setProductos] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(true);
  const [errorProductos, setErrorProductos] = useState("");

  const [pendientes, setPendientes] = useState([]);
  const [loadingPendientes, setLoadingPendientes] = useState(true);
  const [errorPendientes, setErrorPendientes] = useState("");

  const [todos, setTodos] = useState([]);
  const [loadingTodos, setLoadingTodos] = useState(true);
  const [errorTodos, setErrorTodos] = useState("");

  const [enviarProducto, setEnviarProducto] = useState(null);
  const [recibirTraspaso, setRecibirTraspaso] = useState(null);
  const [historialProducto, setHistorialProducto] = useState(null);
  const [agregarCantidad, setAgregarCantidad] = useState(null);
  const [registrarOpen, setRegistrarOpen] = useState(false);
  const [editarProducto, setEditarProducto] = useState(null);

  const cargarProductos = () => {
    setLoadingProductos(true);
    setErrorProductos("");
    axios.get("/api/inventario-interno/productos")
      .then(({ data }) => setProductos(data?.data || []))
      .catch((e) => setErrorProductos(e?.response?.data?.message || "Error al cargar los productos."))
      .finally(() => setLoadingProductos(false));
  };

  const cargarTodos = () => {
    setLoadingTodos(true);
    setErrorTodos("");
    axios.get("/api/inventario-interno/traspasos")
      .then(({ data }) => setTodos(data?.data || []))
      .catch((e) => setErrorTodos(e?.response?.data?.message || "Error al cargar el historial."))
      .finally(() => setLoadingTodos(false));
  };

  const cargarPendientes = () => {
    if (!user?.sucursal) return;
    setLoadingPendientes(true);
    setErrorPendientes("");
    axios.get("/api/inventario-interno/traspasos", { params: { estado: "Pendiente", sucursal: user.sucursal } })
      .then(({ data }) => setPendientes(data?.data || []))
      .catch((e) => setErrorPendientes(e?.response?.data?.message || "Error al cargar los traspasos pendientes."))
      .finally(() => setLoadingPendientes(false));
  };

  useEffect(() => {
    cargarProductos();
    cargarTodos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    cargarPendientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.sucursal]);

  const recargarTodo = () => {
    cargarProductos();
    cargarPendientes();
    cargarTodos();
  };

  const productosMiSucursal = productos.map((p) => ({
    ...p,
    cantidadAqui: p.cantidades?.[user?.sucursal] ?? 0,
  }));

  return (
    <DashboardLayout>
      <PageHeader title="Inventario Interno" subtitle="Control de herramientas y equipos internos" showBack={false} />
      <Box sx={{ p: 1 }}>
        <Paper variant="outlined" sx={{ mb: 1, overflow: "hidden" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ minHeight: 34, "& .MuiTab-root": { minHeight: 34, py: 0.5, fontSize: 12, fontWeight: 700, textTransform: "none" } }}
          >
            <Tab label="Mi Sucursal" />
            <Tab label="Control" />
          </Tabs>
        </Paper>

        {tab === 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
              Traspasos Pendientes
            </Typography>
            {errorPendientes && <Alert severity="error" sx={{ mb: 1 }}>{errorPendientes}</Alert>}
            {loadingPendientes ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>
            ) : pendientes.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>No hay traspasos pendientes.</Alert>
            ) : (
              <Paper variant="outlined" sx={{ mb: 2 }}>
                <TableContainer>
                  <Table size="small" sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
                    <TableHead>
                      <TableRow>
                        <Th>Producto</Th>
                        <Th>Origen</Th>
                        <Th>Destino</Th>
                        <Th align="center">Cantidad</Th>
                        <Th>Detalle</Th>
                        <Th>Fecha</Th>
                        <Th>Usuario</Th>
                        <Th align="center">Acción</Th>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendientes.map((r) => (
                        <TableRow key={r.id} hover>
                          <TableCell>{r.producto_nombre}</TableCell>
                          <TableCell>{r.id_sucursal_origen}</TableCell>
                          <TableCell>{r.id_sucursal_destino}</TableCell>
                          <TableCell align="center">{r.cantidad}</TableCell>
                          <TableCell>{r.detalle || "-"}</TableCell>
                          <TableCell>{formatFechaHora(r.fecha_registro)}</TableCell>
                          <TableCell>{r.usuario}</TableCell>
                          <TableCell align="center">
                            {r.id_sucursal_destino === user?.sucursal ? (
                              <Button
                                size="small" variant="outlined" color="success"
                                startIcon={<CheckIcon fontSize="small" />}
                                onClick={() => setRecibirTraspaso(r)}
                              >
                                Recibir
                              </Button>
                            ) : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
              Inventario en {user?.sucursal || "mi sucursal"}
            </Typography>
            {errorProductos && <Alert severity="error" sx={{ mb: 1 }}>{errorProductos}</Alert>}
            {loadingProductos ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>
            ) : productosMiSucursal.length === 0 ? (
              <Alert severity="info">No hay productos registrados.</Alert>
            ) : (
              <Paper variant="outlined">
                <TableContainer>
                  <Table size="small" sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
                    <TableHead>
                      <TableRow>
                        <Th>Nombre</Th>
                        <Th>Marca</Th>
                        <Th>Detalle</Th>
                        <Th align="center">Cantidad</Th>
                        <Th align="center">Acciones</Th>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productosMiSucursal.map((p) => (
                        <TableRow key={p.id} hover>
                          <TableCell>{p.nombre}</TableCell>
                          <TableCell>{p.marca || "-"}</TableCell>
                          <TableCell>{p.detalle || "-"}</TableCell>
                          <TableCell align="center">{p.cantidadAqui}</TableCell>
                          <TableCell align="center">
                            <Tooltip title={p.cantidadAqui <= 0 ? "Sin stock disponible" : "Enviar a otra sucursal"}>
                              <span>
                                <IconButton
                                  size="small" color="primary" disabled={p.cantidadAqui <= 0}
                                  onClick={() => setEnviarProducto({ id: p.id, nombre: p.nombre, cantidadDisponible: p.cantidadAqui })}
                                >
                                  <SendIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Historial">
                              <IconButton size="small" onClick={() => setHistorialProducto({ id: p.id, nombre: p.nombre })}>
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}

        {tab === 1 && (
          <>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setRegistrarOpen(true)}>
                Registrar producto
              </Button>
            </Box>

            {errorProductos && <Alert severity="error" sx={{ mb: 1 }}>{errorProductos}</Alert>}
            {loadingProductos ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>
            ) : productos.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>No hay productos registrados.</Alert>
            ) : (
              <Paper variant="outlined" sx={{ mb: 2 }}>
                <TableContainer>
                  <Table size="small" sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
                    <TableHead>
                      <TableRow>
                        <Th>Nombre</Th>
                        <Th>Marca</Th>
                        <Th>Detalle</Th>
                        {SUCURSALES.map((s) => <Th key={s} align="center">{s}</Th>)}
                        <Th align="center">Editar</Th>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productos.map((p) => (
                        <TableRow key={p.id} hover>
                          <TableCell>{p.nombre}</TableCell>
                          <TableCell>{p.marca || "-"}</TableCell>
                          <TableCell>{p.detalle || "-"}</TableCell>
                          {SUCURSALES.map((s) => (
                            <TableCell key={s} align="center">
                              <Box
                                component="button" type="button"
                                onClick={() => setAgregarCantidad({ producto: { id: p.id, nombre: p.nombre }, sucursal: s })}
                                sx={{ border: 0, background: "transparent", color: "#006b75", cursor: "pointer", font: "inherit", fontWeight: 600 }}
                              >
                                {p.cantidades?.[s] ?? 0}
                              </Box>
                            </TableCell>
                          ))}
                          <TableCell align="center">
                            <Tooltip title="Editar nombre y detalle">
                              <IconButton size="small" onClick={() => setEditarProducto(p)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
              Historial completo
            </Typography>
            {errorTodos && <Alert severity="error" sx={{ mb: 1 }}>{errorTodos}</Alert>}
            {loadingTodos ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>
            ) : todos.length === 0 ? (
              <Alert severity="info">No hay movimientos registrados.</Alert>
            ) : (
              <Paper variant="outlined">
                <TableContainer>
                  <Table size="small" sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
                    <TableHead>
                      <TableRow>
                        <Th>Producto</Th>
                        <Th>Origen</Th>
                        <Th>Destino</Th>
                        <Th align="center">Cantidad</Th>
                        <Th>Detalle</Th>
                        <Th>Fecha</Th>
                        <Th>Usuario</Th>
                        <Th align="center">Tipo</Th>
                        <Th align="center">Estado</Th>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {todos.map((r) => (
                        <TableRow key={r.id} hover>
                          <TableCell>{r.producto_nombre}</TableCell>
                          <TableCell>{r.id_sucursal_origen}</TableCell>
                          <TableCell>{r.id_sucursal_destino}</TableCell>
                          <TableCell align="center">{r.cantidad}</TableCell>
                          <TableCell sx={{ maxWidth: 220, whiteSpace: "pre-wrap" }}>{r.detalle || "-"}</TableCell>
                          <TableCell>{formatFechaHora(r.fecha_registro)}</TableCell>
                          <TableCell>{r.usuario}</TableCell>
                          <TableCell align="center">{r.tipo}</TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small" label={r.estado}
                              color={r.estado === "Pendiente" ? "warning" : "success"}
                              sx={{ height: 19, fontSize: "0.68rem" }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}
      </Box>

      <EnviarTraspasoDialog
        open={Boolean(enviarProducto)}
        producto={enviarProducto}
        sucursalOrigen={user?.sucursal}
        onClose={() => setEnviarProducto(null)}
        onEnviado={() => { setEnviarProducto(null); recargarTodo(); }}
      />

      <RecibirTraspasoDialog
        open={Boolean(recibirTraspaso)}
        traspaso={recibirTraspaso}
        onClose={() => setRecibirTraspaso(null)}
        onRecibido={() => { setRecibirTraspaso(null); recargarTodo(); }}
      />

      <RegistrarProductoDialog
        open={registrarOpen}
        onClose={() => setRegistrarOpen(false)}
        onCreado={() => { setRegistrarOpen(false); cargarProductos(); }}
      />

      <EditarProductoDialog
        open={Boolean(editarProducto)}
        producto={editarProducto}
        onClose={() => setEditarProducto(null)}
        onEditado={() => { setEditarProducto(null); cargarProductos(); }}
      />

      <AgregarCantidadDialog
        open={Boolean(agregarCantidad)}
        producto={agregarCantidad?.producto}
        sucursal={agregarCantidad?.sucursal}
        onClose={() => setAgregarCantidad(null)}
        onAgregado={() => { setAgregarCantidad(null); recargarTodo(); }}
      />

      <HistorialProductoDialog
        open={Boolean(historialProducto)}
        productoId={historialProducto?.id}
        productoNombre={historialProducto?.nombre}
        onClose={() => setHistorialProducto(null)}
      />
    </DashboardLayout>
  );
};

export default InventarioInternoPage;
